/**
 * Conformance-edge overlay — draws the "telemetry-only" drift: observed calls
 * the model doesn't document, as an amber dashed violation arc between the two
 * node rects (with an arrowhead + an "undocumented" tag). Complements the
 * edge-health layer, which only styles MODELED edges; an undocumented edge has
 * no board edge / route, so this layer synthesizes the geometry from the node
 * positions. A board-layer plugin CONTRIBUTION — the board never mounts it.
 *
 * No-ops when telemetry is idle or nothing drifts, so a conformant board stays
 * clean.
 */
import { memo } from "react";
import type { BoardEdge, BoardItem, PlannedRoute } from "@archlang/scene/board";
import { useLiveEdgeStats } from "../liveStatsStore.js";
import { undocumentedObserved, center, rectBorderPoint, arcControl } from "./conformanceEdges.js";

/** Amber (amber-500) — the "observed but undocumented" violation color. */
const VIOLATION = "#f59e0b";

export interface ConformanceEdgeLayerProps {
  readonly edges: readonly BoardEdge[];
  readonly routes: ReadonlyMap<string, PlannedRoute>;
  readonly nodes: ReadonlyMap<string, BoardItem>;
}

function ConformanceEdgeLayerImpl({ edges, nodes }: ConformanceEdgeLayerProps) {
  const stats = useLiveEdgeStats();
  const violations = undocumentedObserved(edges, stats, nodes);
  if (violations.length === 0) return <g className="conformance-edge-layer" />;

  return (
    <g className="conformance-edge-layer" style={{ pointerEvents: "none" }}>
      {violations.map((v) => {
        const ca = center(v.from);
        const cb = center(v.to);
        const p1 = rectBorderPoint(v.from, cb.x, cb.y);
        const p2 = rectBorderPoint(v.to, ca.x, ca.y);
        const ctrl = arcControl(p1, p2);
        const d = `M ${p1.x} ${p1.y} Q ${ctrl.x} ${ctrl.y} ${p2.x} ${p2.y}`;

        // Arrowhead at p2, oriented along the arc tangent (ctrl → p2).
        const ang = Math.atan2(p2.y - ctrl.y, p2.x - ctrl.x);
        const ah = 10;
        const aw = 5;
        const bx = p2.x - Math.cos(ang) * ah;
        const by = p2.y - Math.sin(ang) * ah;
        const nx = -Math.sin(ang);
        const ny = Math.cos(ang);
        const arrow = `${p2.x},${p2.y} ${bx + nx * aw},${by + ny * aw} ${bx - nx * aw},${by - ny * aw}`;

        // Tag at the arc midpoint (quadratic at t=0.5).
        const mx = 0.25 * p1.x + 0.5 * ctrl.x + 0.25 * p2.x;
        const my = 0.25 * p1.y + 0.5 * ctrl.y + 0.25 * p2.y;

        return (
          <g key={v.key} data-conformance="undocumented">
            <path
              d={d}
              fill="none"
              stroke={VIOLATION}
              strokeWidth={2}
              strokeDasharray="6 4"
              strokeLinecap="round"
              opacity={0.95}
            />
            <polygon points={arrow} fill={VIOLATION} />
            <g transform={`translate(${mx} ${my})`}>
              <rect x={-44} y={-9} width={88} height={17} rx={8} fill="#1c1206" stroke={VIOLATION} strokeWidth={0.75} />
              <text x={0} y={3} textAnchor="middle" fontSize={10} fontWeight={600} fill={VIOLATION}>
                undocumented
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
}

export const ConformanceEdgeLayer = memo(ConformanceEdgeLayerImpl);
